import {
  AiContentTextConsistencyMode,
  RemoteAiContentGenerationSetup,
  RemoteAiContentImageAiGenerationSetup,
  RemoteAiContentImageEditSetup,
  RemoteAiContentImageSmartCropSetup,
  RemoteAiContentProductShotSetup,
  RemoteAiContentTextGenerationSetup,
  imageEditModeUiNames,
} from 'common/src/modules/contentFeeds/contentFeeds.aiContentTypes';
import {ProductShotType} from 'common/src/modules/aiImages/aiImages.enums';

export interface GenerationPipelineDescriptor {
  getInputColumns(): string[];
  formatSummary(): string;
}

function formatInputColumns(columns: string[]): string {
  if (columns.length === 0) return '';

  return `. Input columns participating in AI generation: ${columns.join(', ')}`;
}

function createTextDescriptor(setup: RemoteAiContentTextGenerationSetup): GenerationPipelineDescriptor {
  return {
    getInputColumns() {
      return setup.usedInputColumns?.map((col) => col.columnName) ?? [];
    },
    formatSummary() {
      const mode =
        setup.textConsistencyMode === AiContentTextConsistencyMode.Adapted
          ? 'Adapted per size'
          : 'Identical across sizes';
      const adapted = setup.adaptedAdSizes?.length
        ? ` (except ${setup.adaptedAdSizes.join(', ')})`
        : '';
      const inputs = formatInputColumns(this.getInputColumns());

      return `Text Generation. Text consistency: ${mode}${adapted}${inputs}`;
    },
  };
}

function createImageAiDescriptor(
  setup: RemoteAiContentImageAiGenerationSetup,
): GenerationPipelineDescriptor {
  return {
    getInputColumns() {
      return setup.usedInputColumns?.map((col) => col.columnName) ?? [];
    },
    formatSummary() {
      let description = `Prompt to Image (with optional reference images)${formatInputColumns(this.getInputColumns())}`;

      if (setup.useSharedIntermediateImage) {
        description +=
          '. Shared image in use (a single large image will be generated and smartly cropped to all ad sizes)';
      }

      return description;
    },
  };
}

function createProductShotDescriptor(
  setup: RemoteAiContentProductShotSetup,
): GenerationPipelineDescriptor {
  return {
    getInputColumns() {
      const columns: string[] = [];

      if (setup.productImageInputColumnName) {
        columns.push(setup.productImageInputColumnName);
      }

      if (setup.ScenePromptInputColumnName) {
        setup.ScenePromptInputColumnName.forEach((col) => columns.push(col.columnName));
      }

      if (setup.OptionalSceneReferenceImageInputColumnName) {
        columns.push(setup.OptionalSceneReferenceImageInputColumnName);
      }

      return columns;
    },
    formatSummary() {
      const productTypeName = setup.productType === ProductShotType.Vehicle ? 'Vehicle' : 'Product';
      const sceneSource = setup.ScenePromptInputColumnName?.length
        ? 'prompt'
        : setup.OptionalSceneReferenceImageInputColumnName
          ? 'reference image'
          : 'N/A';

      let description = `Product Shot (${productTypeName}, scene from ${sceneSource}, margin: ${setup.marginPercent}%)${formatInputColumns(this.getInputColumns())}`;

      if (setup.useSharedIntermediateImage) {
        description += '. Shared image in use';
      }

      return description;
    },
  };
}

function createSmartCropDescriptor(
  setup: RemoteAiContentImageSmartCropSetup,
): GenerationPipelineDescriptor {
  return {
    getInputColumns() {
      return setup.inputColumnName ? [setup.inputColumnName] : [];
    },
    formatSummary() {
      return `Image Smart Crop (object: ${setup.objectDetectPrompt || 'N/A'}, margin: ${setup.marginPercent}%)${formatInputColumns(this.getInputColumns())}`;
    },
  };
}

function createImageEditDescriptor(
  setup: RemoteAiContentImageEditSetup,
): GenerationPipelineDescriptor {
  return {
    getInputColumns() {
      const columns: string[] = [];
      if (setup.inputImageColumnName) columns.push(setup.inputImageColumnName);
      setup.stylePromptInputColumns?.forEach((col) => columns.push(col.columnName));

      return columns;
    },
    formatSummary() {
      return `Image Edit (${imageEditModeUiNames[setup.editMode]})${formatInputColumns(this.getInputColumns())}`;
    },
  };
}

export function getGenerationPipelineDescriptor(
  setup: RemoteAiContentGenerationSetup | null | undefined,
): GenerationPipelineDescriptor | null {
  if (!setup) return null;
  if (setup.textGeneration) return createTextDescriptor(setup.textGeneration);
  if (setup.imageAiGeneration) return createImageAiDescriptor(setup.imageAiGeneration);
  if (setup.productShotGeneration) return createProductShotDescriptor(setup.productShotGeneration);
  if (setup.imageSmartCrop) return createSmartCropDescriptor(setup.imageSmartCrop);
  if (setup.imageEdit) return createImageEditDescriptor(setup.imageEdit);

  return null;
}
